import { inject, needs } from "fw";
import { dispatch } from "fw-state";

import { SearchStore, SearchType, UpdateSearchFiltersAction, SetSearchPageAction, SetSearchSortAction, SetSearchTypeAction } from "state/search";

import { AccountType, AtsOrganization } from "models/ats-organization";
import { AtsUser } from "models/ats-user";

import { Pager } from "views/components/pager";
import { SearchItemModal } from "./search-item-modal";
import { CurrentUserStore } from "state/current-user";
import { UserType } from "models/user";

@inject
@needs(SearchItemModal, Pager)
export class Search {
    private loading: boolean = false;
    private searchType: SearchType = SearchType.Users;
    private searchTerms = {
        [SearchType.Users]: "",
        [SearchType.Organizations]: ""
    };
    private isModalOpen: boolean = false;
    private selectedRow: {} = null;

    public constructor(
        private store: SearchStore,
        private currentUser: CurrentUserStore
    ) {}

    public async activate() {
        if (this.store.state.type != null) {
            this.searchType = this.store.state.type;
        }
        this.searchTerms = { ...this.store.state.searchTerms };
    }

    get searchTypes(): string[] {
        return Object.values(SearchType);
    }

    get searchText() {
        return this.searchTerms[this.searchType];
    }

    public async setType(s: SearchType) {
        if (this.searchType == s) return;
        this.searchType = s;

        // Save the search type to localStorage for persistence
        await dispatch(new SetSearchTypeAction(s));

        this.find();
    }

    find() {
        if (this.searchText.length) {
            this.loading = true;
            dispatch(new UpdateSearchFiltersAction(this.searchType, null, this.searchTerms, null));
            this.loading = false;
        } else {
            this.store.resetState();
        }
    }

    get gridData(): AtsOrganization[] | AtsUser[] {
        const data = [];
        this.loading = true;

        this.store.state.collection.forEach((item) => {
            switch (item.constructor) {
                case AtsOrganization:
                    data.push(item as AtsOrganization);
                    break;

                case AtsUser:
                    data.push(item as AtsUser);
                    break;
            }
        });

        this.loading = false;
        return data;
    }

    openModal(row: AtsUser | AtsOrganization) {
        this.selectedRow = row;
        this.isModalOpen = true;
    }

    closeModal() {
        this.selectedRow = null;
        this.isModalOpen = false;
    }

    public async setPage(page: number) {
        this.loading = true;
        await dispatch(new SetSearchPageAction(page));
        this.loading = false;
    }

    get currentPage() {
        return this.store.state.currentPage || 1;
    }

    get sortColumn(): string {
        return this.store.state.sort ? this.store.state.sort.replace(/[-()]/gi, "") : "";
    }

    get sortOrder(): string {
        return this.store.state.sort && this.store.state.sort.charAt(0) === "-" ? "DESC" : "ASC";
    }

    setSort(sort: string) {
        dispatch(new SetSearchSortAction(sort));
    }

    public get canViewSearch(): boolean {
        switch (this.currentUser.state.user.Type) {
            case UserType.TransferPlannerAPIService:
                return false;
            default:
                return true;
        }
    }

    public openApiDocumentationLink() {
        window.open("/api-docs/index.html", "_blank");
    }

    public getAccountType = (value: AccountType | null): string => {
        return AccountType[value];
    };
}
